import React from 'react';
import { useFetchMediaQuery } from '../redux/api/mediaApi';
import { Button, Table } from 'flowbite-react';
import { TableCell, TableCommon } from '../components/Table/Table';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../redux/store';
import MediaModal from '../components/MediaModal/MediaModal';
import AddMediaModal from '../components/MediaModal/AddMediaModal';
import { Loading } from '../components/Shared/Loading/Loading';
import { PageHeader } from '../components/Shared/PageHeader';
import { closeModal, openModal } from '../redux/slices/modalMediaSlice';
import { usePagination } from '../hooks/usePagination';
import { IMedia } from '../common/interfaces/Media.interface';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { usePageTitle } from '../hooks/usePageTitle';
import { selectIsDarkMode } from '../redux/selectors/displaySettingsSelectors';
import { useAppSelector } from '../redux/hooks/useAppSelector';

export const MediaPage: React.FC = () => {
  const { t } = useTranslation();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  usePageTitle(t('pageTitles.media'));

  const { currentPage, setCurrentPage, size } = usePagination();
  const {
    data: response,
    isLoading,
    isError,
    error: loadError,
    refetch,
  } = useFetchMediaQuery({
    page: currentPage,
    size,
  });

  const mediaData: IMedia[] = response?.data || [];
  const columnHeadLabels = [
    { id: 'name', label: t('media.name') },
    { id: 'type', label: t('media.type') },
    { id: 'url', label: t('media.url') },
    { id: 'preview', label: t('media.preview') },
  ];

  const dispatch = useDispatch();
  const { modalType, selectedItem } = useSelector(
    (state: RootState) => state.modal
  );

  if (isError) return <ErrorComponent error={loadError} onRetry={refetch} />;
  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title={t('media.media')}
        buttonText={t('media.addMedia')}
        buttonOnClick={() => dispatch(openModal({ modalType: 'add' }))}
      />
      <TableCommon columns={columnHeadLabels}>
        {mediaData.map((item) => (
          <Table.Row key={item.id} className="bg-white">
            <TableCell classes="max-w-xs text-start">{item.name}</TableCell>
            <TableCell>{t(`media.${item.type.toLowerCase()}`)}</TableCell>
            <TableCell>
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 underline hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200"
              >
                {t('media.url')}
              </a>
            </TableCell>
            <TableCell>
              <Button
                size="xs"
                className={`m-auto ${buttonClassName}`}
                onClick={() =>
                  dispatch(openModal({ modalType: 'view', selectedItem: item }))
                }
              >
                {t('media.preview')}
              </Button>
            </TableCell>
          </Table.Row>
        ))}
      </TableCommon>
      {/* <CustomPagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      /> */}
      {modalType === 'view' && (
        <MediaModal
          isOpen={true}
          item={selectedItem}
          onClose={() => dispatch(closeModal())}
        />
      )}
      {modalType === 'add' && (
        <AddMediaModal isOpen={true} onClose={() => dispatch(closeModal())} />
      )}
    </>
  );
};
